import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook de timeout por inactividad y absoluto.
 * - idleMs: tiempo sin actividad antes de disparar onIdle (ej. 30 min)
 * - absoluteMs: tiempo total desde mount antes de onAbsolute (ej. 8h)
 * Se reinicia con eventos de actividad: mousemove, keydown, click, scroll, touchstart.
 */
export function useIdleTimeout({
  idleMs = 30 * 60 * 1000,
  absoluteMs = 8 * 60 * 60 * 1000,
  onIdle,
  onAbsolute,
  enabled = true,
}: {
  idleMs?: number;
  absoluteMs?: number;
  onIdle: () => void;
  onAbsolute?: () => void;
  enabled?: boolean;
}) {
  const idleTimer = useRef<number | null>(null);
  const absoluteTimer = useRef<number | null>(null);

  const resetIdle = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      onIdle();
    }, idleMs);
  }, [idleMs, onIdle]);

  useEffect(() => {
    if (!enabled) return;
    const events: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const handler = () => resetIdle();
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    resetIdle();

    if (absoluteMs && onAbsolute) {
      absoluteTimer.current = window.setTimeout(() => {
        onAbsolute();
      }, absoluteMs);
    }

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      if (absoluteTimer.current) window.clearTimeout(absoluteTimer.current);
    };
  }, [enabled, resetIdle, absoluteMs, onAbsolute]);
}
